import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import IncognitoIcon from '@common/assets/icons/incognito.svg?react';
import { Badge, type BadgeProps } from '@common/ui/Badge';
import { Button } from '@common/ui/buttons/Button';
import { DynamicImage } from '@common/ui/DynamicImage';
import { Loader } from '@common/ui/Loader';

import { disableFeatureOnError, useAccountStore } from '@entities/account';

import { useGetMatchmakerRecommendations } from '@services/hooks/MatchmakerService';
import {
  type MatchmakerRecommendation,
  type MatchmakerRecommendationState,
} from '@services/MatchmakerService';

import styles from './MyRecommendations.module.scss';

const STATE_BADGES: Record<
  MatchmakerRecommendationState,
  { text: string; color: BadgeProps['color'] }
> = {
  sent: { text: 'Отправлено', color: 'neutralSecondary' },
  opened: { text: 'Открыто', color: 'actionSecondary' },
  joined: { text: 'Друг в Kiss', color: 'positiveSecondary' },
};

const DATE_FORMAT = new Intl.DateTimeFormat('ru-RU', {
  day: 'numeric',
  month: 'long',
});

const SWR_OPTIONS = {
  revalidateOnFocus: false,
  shouldRetryOnError: false,
};

const candidateTitleOf = ({ candidate }: MatchmakerRecommendation) =>
  candidate
    ? [candidate.username, candidate.age]
        .filter((part) => part !== null)
        .join(', ')
    : null;

const RecommendationRow = ({
  recommendation,
}: {
  recommendation: MatchmakerRecommendation;
}) => {
  const title = candidateTitleOf(recommendation);
  const photo = recommendation.candidate?.photo;
  const badge = STATE_BADGES[recommendation.state];

  return (
    <li className={styles.row} data-test-id="my-recommendations-row">
      {photo ? (
        <DynamicImage
          testId="my-recommendations-photo"
          src={photo}
          alt=""
          loading="lazy"
          className={styles.photo}
        />
      ) : (
        <div className={styles.placeholder} aria-hidden="true">
          <IncognitoIcon />
        </div>
      )}
      <div className={styles.rowText}>
        {title ? (
          <div
            className={`${styles.name} ym-hide-content`}
            data-test-id="my-recommendations-name"
          >
            {title}
          </div>
        ) : (
          <div
            className={styles.unavailable}
            data-test-id="my-recommendations-unavailable"
          >
            Анкета недоступна
          </div>
        )}
        <div className={styles.date} data-test-id="my-recommendations-date">
          {DATE_FORMAT.format(new Date(recommendation.created_at))}
        </div>
      </div>
      <Badge
        testId="my-recommendations-state"
        color={badge.color}
        text={badge.text}
      />
    </li>
  );
};

type RecommendationsPageProps = {
  cursor?: string;
  isLast: boolean;
  onFeatureDisabled: () => void;
  onNext: (cursor: string) => void;
};

const RecommendationsPage = ({
  cursor,
  isLast,
  onFeatureDisabled,
  onNext,
}: RecommendationsPageProps) => {
  const { data, error, isLoading, mutate } = useGetMatchmakerRecommendations({
    isEnabled: true,
    queryParams: cursor ? { cursor } : undefined,
    swrOptions: SWR_OPTIONS,
  });
  const [ref, inView] = useInView();
  const nextCursor = data?.next_cursor ?? null;

  useEffect(() => {
    if (error && disableFeatureOnError(error, 'matchmaker')) {
      onFeatureDisabled();
    }
  }, [error]);

  useEffect(() => {
    if (isLast && inView && nextCursor) onNext(nextCursor);
  }, [inView, isLast, nextCursor]);

  if (isLoading) {
    return (
      <li>
        <Loader />
      </li>
    );
  }

  if (error) {
    return (
      <li className={styles.failed} data-test-id="my-recommendations-failed">
        <span>Не удалось загрузить</span>
        <Button
          testId="my-recommendations-retry-button"
          size={400}
          variant="actionSecondary"
          onClick={() => void mutate()}
        >
          Повторить
        </Button>
      </li>
    );
  }

  if (!cursor && data?.results.length === 0) {
    return (
      <li className={styles.empty} data-test-id="my-recommendations-empty">
        Здесь появятся анкеты, которые вы посоветуете
      </li>
    );
  }

  return (
    <>
      {(data?.results ?? []).map((recommendation) => (
        <RecommendationRow
          key={recommendation.invite_id}
          recommendation={recommendation}
        />
      ))}
      {isLast && nextCursor && (
        <li ref={ref} data-test-id="my-recommendations-more" />
      )}
    </>
  );
};

/** «Мои рекомендации» (board 3.5): what became of each invite. */
export const MyRecommendations = () => {
  const isFeatureOn = useAccountStore(
    (state) => !!state.account?.features.matchmaker,
  );
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [isFeatureDisabled, setIsFeatureDisabled] = useState(false);

  const isAvailable = isFeatureOn && !isFeatureDisabled;

  return (
    <div className={styles.myRecommendations} data-test-id="my-recommendations">
      <h1 className={styles.title}>Мои рекомендации</h1>
      <p className={styles.text}>
        Видно, открыл ли друг ссылку и пришёл ли в Kiss
      </p>

      {isAvailable ? (
        <ul className={styles.list}>
          {cursors.map((cursor, index) => (
            <RecommendationsPage
              key={cursor ?? 'first'}
              cursor={cursor}
              isLast={index === cursors.length - 1}
              onFeatureDisabled={() => setIsFeatureDisabled(true)}
              onNext={(next) =>
                setCursors((previous) =>
                  previous.includes(next) ? previous : [...previous, next],
                )
              }
            />
          ))}
        </ul>
      ) : (
        <p
          className={styles.text}
          data-test-id="my-recommendations-unavailable-feature"
        >
          Режим свахи сейчас недоступен
        </p>
      )}

      <p className={styles.footer}>
        Что было дальше между другом и анкетой, мы не показываем — это их
        переписка.
      </p>
    </div>
  );
};
